/* eslint-disable max-len */
import { extendMoment } from 'moment-range';
import m from 'moment-timezone';
import { useMemo, useState } from 'react';
// @ts-ignore
var moment = extendMoment(m);

var momentTZ = tz => date => {
  if (!tz) return moment(date);
  return moment(date).tz(tz);
};

var defaultProps = {
  endDate: null,
  startDate: null,
  startTime: 0,
  onEndTimeChange: () => null,
  onStartTimeChange: () => null,
  onEndDateChange: () => null,
  onStartDateChange: () => null,
  endTime: 0,
  reservations: undefined,
  maxHours: 720,
  timezone: ''
};
export var useDTRP = function useDTRP(props) {
  if (props === void 0) {
    props = defaultProps;
  }

  var [focusedInput, setFocusedInput] = useState(null);
  var startDate = useMemo(() => {
    if (!props.startDate) return props.startDate;
    return props.startDate.clone().hours(props.startTime).startOf('h');
  }, [props.startDate, props.startTime]);
  var endDate = useMemo(() => {
    if (!props.endDate) return props.endDate;
    return props.endDate.clone().hours(props.endTime).startOf('h');
  }, [props.endDate, props.endTime]);
  var momento = useMemo(() => momentTZ(props.timezone), [props.timezone]);

  var onDatesChange = draft => {
    var _draft$startDate, _draft$endDate, _props$reservations;

    // user has not yet selected both dates
    if (!draft.startDate || !draft.endDate) {
      props.onStartDateChange(draft.startDate);
      props.onEndDateChange(draft.endDate);
      return;
    } // user selects same start and end date


    if (draft.startDate.isSame(draft.endDate, 'D')) {
      if (props.startTime >= props.endTime) {
        props.onEndTimeChange(props.startTime + 1);
      }

      props.onStartDateChange(draft.startDate);
      props.onEndDateChange(draft.endDate);
      return;
    }

    var adjustedStartDraft = (_draft$startDate = draft.startDate) == null ? void 0 : _draft$startDate.clone().endOf('D');
    var adjustedEndDraft = (_draft$endDate = draft.endDate) == null ? void 0 : _draft$endDate.clone().startOf('D'); // check if either startDate or endDate is between any reservation time periods

    var shouldResetDate = (_props$reservations = props.reservations) == null ? void 0 : _props$reservations.some(RES => {
      var conflictingStart = momento(RES.start).isBetween(adjustedStartDraft, adjustedEndDraft);
      var conflictingEnd = momento(RES.end).isBetween(adjustedStartDraft, adjustedEndDraft);
      return conflictingStart || conflictingEnd;
    }); // above condition is met; reset endDate

    if (shouldResetDate) {
      props.onStartDateChange(draft.startDate);
      props.onEndDateChange(null);
      return;
    }

    props.onStartDateChange(draft.startDate);
    props.onEndDateChange(draft.endDate);
  };

  var onTimesChange = value => {
    var _props, _props$startDate, _props2;

    if (focusedInput === 'endDate') {
      return props.onEndTimeChange(value);
    }

    props.onStartTimeChange(value); // user has selected same start/end date + draft startTime is greater than existing endTime

    var sameStartEndDate = (_props = props) == null ? void 0 : (_props$startDate = _props.startDate) == null ? void 0 : _props$startDate.isSame((_props2 = props) == null ? void 0 : _props2.endDate, 'D');
    var selectedTimeIsPastEndTime = value >= props.endTime;

    if (sameStartEndDate && selectedTimeIsPastEndTime) {
      props.onEndTimeChange(value + 1);
    }
  }; // unavailable times for start date


  var unavailableStartTimes = (() => {
    var _props3, _props3$reservations;

    if (!startDate) return [];
    var currentDate = momento();

    if (startDate.isSame(currentDate, 'D')) {
      var _props$reservations2;

      var _unavailableTimes = [];
      var earliestSelectableTime = currentDate.add(1, 'h').hour();

      for (var h = 0; h < earliestSelectableTime; h += 1) {
        _unavailableTimes.push(h);
      }

      if (!((_props$reservations2 = props.reservations) != null && _props$reservations2.length)) return _unavailableTimes;
      props.reservations.forEach(RES => {
        var startOfRes = momento(RES.start);
        var endOfRes = momento(RES.end);
        if (!startOfRes.isSame(startDate, 'D')) return;

        if (endOfRes.isSame(startDate, 'D')) {
          var earliestBlockedTime = startOfRes.hour() - 1;
          var latestBlockedTime = endOfRes.hour();

          for (var _h = earliestBlockedTime; _h <= latestBlockedTime; _h += 1) {
            _unavailableTimes.push(_h);
          }
        } else {
          var _earliestBlockedTime = startOfRes.hour();

          for (var _h2 = _earliestBlockedTime; _h2 <= 24; _h2 += 1) {
            _unavailableTimes.push(_h2);
          }
        }
      });
      return _unavailableTimes;
    }

    if (!((_props3 = props) != null && (_props3$reservations = _props3.reservations) != null && _props3$reservations.length)) return [];
    var unavailableTimes = props.reservations.reduce((UAT, RES) => {
      var endOfRes = momento(RES.end);
      var startOfRes = momento(RES.start);
      var sameAsEndRes = startDate.isSame(endOfRes, 'D');
      var sameAsStartRes = startDate.isSame(startOfRes, 'D');

      if (sameAsStartRes && !sameAsEndRes) {
        var latestSelectableTime = startOfRes.subtract(1, 'h').hour();

        for (var _h3 = 24; _h3 > latestSelectableTime - 1; _h3 -= 1) {
          UAT.push(_h3);
        }
      }

      if (!sameAsStartRes && sameAsEndRes) {
        var _earliestSelectableTime = endOfRes.add(1, 'h').hour();

        for (var _h4 = 0; _h4 < _earliestSelectableTime; _h4 += 1) {
          UAT.push(_h4);
        }
      }

      if (sameAsStartRes && sameAsEndRes) {
        var earliestBlockedTime = startOfRes.hour();
        var latestBlockedTime = endOfRes.hour();

        for (var _h5 = earliestBlockedTime; _h5 <= latestBlockedTime; _h5 += 1) {
          UAT.push(_h5);
        }
      }

      return UAT;
    }, []);
    return unavailableTimes;
  })(); // unavailable times for end date


  var unavailableEndTimes = (() => {
    var _props4, _props4$reservations;

    if (!startDate || !endDate) return [];

    if (!((_props4 = props) != null && (_props4$reservations = _props4.reservations) != null && _props4$reservations.length)) {
      if (!endDate.isSame(startDate, 'D')) return [];
      var _unavailableTimes2 = [];

      for (var i = 0; i < props.startTime + 1; i += 1) {
        _unavailableTimes2.push(i);
      }

      return _unavailableTimes2;
    }

    var unavailableTimes = props.reservations.reduce((UAT, RES) => {
      var startOfRes = momento(RES.start);
      var endOfRes = momento(RES.end);

      if (!endDate.isSame(startOfRes, 'D')) {
        if (!startDate.isSame(endDate, 'D')) return UAT;

        for (var _i = 0; _i < props.startTime + 1; _i += 1) {
          UAT.push(_i);
        }

        return UAT;
      }

      if (!startDate.isSame(endDate, 'D')) {
        var latestSelectableTime = startOfRes.subtract(1, 'h').hour();

        for (var _i2 = 24; _i2 > latestSelectableTime; _i2 -= 1) {
          UAT.push(_i2);
        }

        return UAT;
      }

      if (props.startTime > endOfRes.hour()) {
        for (var _i3 = 0; _i3 < props.startTime + 1; _i3 += 1) {
          UAT.push(_i3);
        }
      } else {
        var _latestSelectableTime = startOfRes.subtract(1, 'h').hour();

        for (var _i4 = 24; _i4 > _latestSelectableTime; _i4 -= 1) {
          UAT.push(_i4);
        }

        for (var _i5 = 0; _i5 < props.startTime + 1; _i5 += 1) {
          UAT.push(_i5);
        }
      }

      return UAT;
    }, []);
    return unavailableTimes;
  })(); // disabled dates for start


  var isOutsideStartRange = testDate => {
    var _props5, _props5$reservations, _props6;

    if (testDate.endOf('D').isBefore(momento(), 'd')) return true;
    var adjustedStartOf = testDate.clone().startOf('day');
    var adjustedEndOf = testDate.clone().endOf('day');
    if (!((_props5 = props) != null && (_props5$reservations = _props5.reservations) != null && _props5$reservations.length)) return false;
    var inBetweenRes = (_props6 = props) == null ? void 0 : _props6.reservations.some(RES => {
      var resStartDate = momento(RES.start);
      var resEndDate = momento(RES.end);
      var isBeforeAdjusted = adjustedStartOf.isBefore(resStartDate);
      var isAfterAdjusted = adjustedEndOf.isAfter(resEndDate);
      if (isBeforeAdjusted || isAfterAdjusted) return false;
      return adjustedStartOf.isBetween(resStartDate, resEndDate);
    });
    return inBetweenRes;
  }; // disabled dates for end


  var isOutsideEndRange = testDate => {
    var _props7, _props7$reservations, _props10;

    var adjustedStartOf = testDate.clone().startOf('D');
    var adjustedEndOf = testDate.clone().endOf('D');
    if (adjustedEndOf.isBefore(momento(), 'd')) return true;

    if (!((_props7 = props) != null && (_props7$reservations = _props7.reservations) != null && _props7$reservations.length)) {
      var _props8;

      if (!((_props8 = props) != null && _props8.maxHours) || !startDate) return false;
      if (testDate.isBefore(startDate, 'D')) return true;

      var _duration = moment.duration(testDate.clone().endOf('D').diff(startDate.clone().hours(props.startTime).startOf('h')));

      return _duration.asHours() > props.maxHours;
    }

    if (!startDate) {
      var _props9;

      var inBetweenRes = (_props9 = props) == null ? void 0 : _props9.reservations.some(RES => {
        var resStartDate = momento(RES.start);
        var resEndDate = momento(RES.end);
        var isBeforeAdjusted = adjustedStartOf.isBefore(resStartDate);
        var isAfterAdjusted = adjustedEndOf.isAfter(resEndDate);
        if (isBeforeAdjusted || isAfterAdjusted) return false;
        return adjustedStartOf.isBetween(resStartDate, resEndDate);
      });
      return inBetweenRes;
    }

    if (adjustedEndOf.isBefore(startDate)) return true; // needs work

    var isBetweenOrAfter = (_props10 = props) == null ? void 0 : _props10.reservations.some(RES => {
      var resStartDate = momento(RES.start).clone();
      var testIsAfterRes = adjustedStartOf.isAfter(resStartDate);
      var startDateIsBeforeRes = startDate.isBefore(resStartDate);
      if (startDateIsBeforeRes && testIsAfterRes) return true;
      return false;
    });
    if (isBetweenOrAfter) return true;
    var duration = moment.duration(testDate.endOf('D').diff(startDate.clone().hours(props.startTime).startOf('h')));
    return duration.asHours() > props.maxHours;
  };

  var state = {
    startDate,
    endDate,
    startTime: props.startTime,
    endTime: props.endTime,
    unavailableStartTimes,
    unavailableEndTimes,
    focusedInput
  };
  var methods = {
    onDatesChange,
    onTimesChange,
    setFocusedInput,
    isOutsideStartRange,
    isOutsideEndRange
  };
  return {
    state,
    methods
  };
};
//# sourceMappingURL=useDateTimeRangePicker.js.map